import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { bulkUploadVideos, getUploadLink, shortsBasicData } from "./api";

// Video bulk upload, used by Video and Shorts: every file goes straight to Vimeo from
// the browser, the same way Create Video does it, then one entry is created per file
// with the file name as title.

const ALLOWED_EXTENSIONS = ["mp4", "ogx", "oga", "ogv", "ogg", "webm", "mkv", "mov"];
const MAX_FILES = 20; // matches VideoUriBulkUploadRequest::MAX_VIDEOS

const getExtension = (name) => name.slice(name.lastIndexOf(".") + 1).toLowerCase();

const getTitle = (name) => {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(0, dot) : name).trim();
};

const useVideoBulkUpload = ({
  endpoint,
  entityLabel = "video",
  requireCategory = false,
  refetch,
  handleClose,
}) => {
  const fileInputRef = useRef(null);
  const cancelRef = useRef(null);
  const [items, setItems] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [categoryError, setCategoryError] = useState("");
  // set when the files reached Vimeo but the entries could not be created
  const [createError, setCreateError] = useState("");

  useEffect(() => {
    if (!requireCategory) return;
    shortsBasicData().then((response) => {
      setCategories(response?.data?.data?.category ?? []);
    });
  }, [requireCategory]);

  const updateItem = (id, changes) =>
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));

  const resetForm = () => {
    setItems([]);
    setIsUploading(false);
    setCategory(null);
    setCategoryError("");
    setCreateError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = "";
    const rejected = [];
    const accepted = [];
    selected.forEach((file, index) => {
      if (!ALLOWED_EXTENSIONS.includes(getExtension(file.name))) {
        rejected.push(file.name);
        return;
      }
      // ignore files already in the list
      if (items.some((item) => item.name === file.name && item.size === file.size)) return;
      accepted.push({
        id: `${Date.now()}-${index}-${file.name}`,
        file,
        name: file.name,
        size: file.size,
        title: getTitle(file.name),
        progress: 0,
        // pending -> uploading -> uploaded | failed | not_created (on Vimeo, but no entry)
        status: "pending",
      });
    });
    if (rejected.length > 0) {
      toast.error(
        `Skipped ${rejected.length} file(s). Allowed: ${ALLOWED_EXTENSIONS.join(", ").toUpperCase()}`
      );
    }
    setItems((prev) => {
      const next = [...prev, ...accepted];
      if (next.length > MAX_FILES) {
        toast.error(`A maximum of ${MAX_FILES} videos can be uploaded at a time.`);
        return next.slice(0, MAX_FILES);
      }
      return next;
    });
  };

  const handleRemove = (id) => setItems((prev) => prev.filter((item) => item.id !== id));

  /** Uploads one file to Vimeo and returns its Vimeo URI. */
  const uploadToVimeo = async (item) => {
    updateItem(item.id, { status: "uploading", progress: 0 });
    const response = await getUploadLink(item.size);
    const uploadLink = response?.data?.data?.upload_link;
    const videoUri = response?.data?.data?.uri;
    if (!uploadLink || !videoUri) throw new Error("Could not get an upload link");

    await axios.patch(uploadLink, item.file, {
      headers: {
        "Content-Type": "application/offset+octet-stream",
        "Tus-Resumable": "1.0.0",
        "Upload-Offset": 0,
      },
      cancelToken: cancelRef.current?.token,
      onUploadProgress: (event) => {
        if (event.total) {
          updateItem(item.id, { progress: Math.round((event.loaded * 100) / event.total) });
        }
      },
    });
    // the URI is kept so a failed create can be retried without uploading the file again
    updateItem(item.id, { status: "uploaded", progress: 100, videoUri });
    return { title: item.title, video_uri: videoUri, file_name: item.name };
  };

  const handleCategoryChange = (option) => {
    setCategory(option);
    setCategoryError("");
  };

  const handleUpload = async () => {
    // already on Vimeo from a failed attempt: these only need the entry created again
    const onVimeo = items.filter((item) => item.status === "not_created");
    const pending = items.filter((item) => item.status === "pending" || item.status === "failed");
    if (pending.length === 0 && onVimeo.length === 0) {
      toast.error("Select at least one video file");
      return;
    }
    if (requireCategory && !category) {
      setCategoryError("*Select a category");
      return;
    }
    setIsUploading(true);
    setCreateError("");
    cancelRef.current = axios.CancelToken.source();

    const uploaded = onVimeo.map((item) => ({
      id: item.id,
      title: item.title,
      video_uri: item.videoUri,
      file_name: item.name,
    }));
    for (const item of pending) {
      try {
        uploaded.push({ id: item.id, ...(await uploadToVimeo(item)) });
      } catch (error) {
        if (axios.isCancel(error)) break;
        updateItem(item.id, { status: "failed" });
        toast.error(`${item.name} could not be uploaded`);
      }
    }

    if (uploaded.length === 0) {
      setIsUploading(false);
      return;
    }

    const response = await bulkUploadVideos(
      endpoint,
      uploaded.map(({ id, ...video }) => video),
      requireCategory ? category?._id : null
    );
    setIsUploading(false);

    if (response?.status === 200 && response?.data?.success) {
      const queued = response?.data?.data?.queued_videos ?? uploaded.length;
      toast.success(
        `${queued} ${entityLabel}(s) uploaded. They are added as inactive; add the remaining details from the list once the video has been processed.`
      );
      resetForm();
      handleClose();
      refetch?.();
    } else {
      const errors = response?.errors ?? response?.data?.errors;
      const message =
        (errors && Object.values(errors).flat()[0]) ??
        response?.message ??
        response?.data?.message ??
        "Something went wrong";
      // the files are on Vimeo with nothing created for them, so the panel keeps them
      // listed and shows why; the message is not a toast because it must not disappear
      const uploadedIds = uploaded.map((video) => video.id);
      setItems((prev) =>
        prev.map((item) =>
          uploadedIds.includes(item.id) ? { ...item, status: "not_created" } : item
        )
      );
      setCreateError(message);
    }
  };

  const handleCancel = () => {
    cancelRef.current?.cancel("Upload canceled by user.");
    resetForm();
    handleClose();
  };

  return {
    fileInputRef,
    items,
    isUploading,
    categories,
    category,
    categoryError,
    createError,
    handleCategoryChange,
    acceptTypes: ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(","),
    handleFileChange,
    handleRemove,
    handleUpload,
    handleCancel,
  };
};

export default useVideoBulkUpload;
